import type { InputFrame } from '../physics/skier'
import { quantiseSteer } from '../race/ghost'
import { clamp, moveTowards } from '../core/math'

/**
 * One thumb. Spec §8:
 *
 *   Drag left/right — steer. Displacement maps to edge angle, not heading.
 *                     Relative drag, so the thumb never hunts for a control.
 *   Push down       — check speed / slarve.
 *   Flick up, or a second-finger tap — pop off a lip.
 *   Everything else — tuck, automatically.
 *
 * The first version put four actions on one thumb with overlapping gestures,
 * and they fought:
 *
 *   - **Tuck and steer wanted the same thumb.** Tuck engaged on holding still
 *     near centre, so any small line correction dropped it — and small line
 *     corrections are most of a run. Tuck is now *automatic*: you tuck unless
 *     you are braking or turning hard. Real racers tuck whenever they can, so
 *     asking the player to request it was a decision with one right answer.
 *   - **The brake fired on the thumb's own arc.** A thumb pivots at its base,
 *     so sweeping left-to-right traces a curve that drifts tens of pixels
 *     vertically — straight through a brake threshold measured from a fixed
 *     origin. The vertical origin now *follows* slow drift and only a
 *     deliberate push outruns it.
 *   - **Steering was linear**, so the fine corrections a 26m corridor needs
 *     sat in the same thumb travel as full lock. There is an expo curve now.
 *   - **Nothing showed where neutral was.** Relative drag has no home
 *     position you can see, so "straight" was a guess. There is a stick.
 */

const TOUCH = {
  /** Screen px of horizontal travel for full edge, at a 400px-wide reference. */
  // Widened with the carve rebuild: the stronger turn law makes mid-range
  // steer genuinely effective, so the throw buys precision now instead of
  // dead travel before an underpowered full lock.
  STEER_RANGE: 116,
  /**
   * Fraction of steer travel ignored around centre. Without it, one pixel of
   * drag quantises to a nonzero steer — which starts the race clock, breaks
   * the auto-tuck, and defeats the released-thumb assist.
   */
  STEER_DEADZONE: 0.05,
  /**
   * Expo blend, 0 = linear, 1 = cubic. Halves the sensitivity around centre
   * while keeping full lock at full travel: a quarter of the travel is ~14%
   * edge for trimming a line, and the last quarter is the whole slarve.
   */
  // Was 0.5 — half cubic. Stacked on the old weak turn law it made the
  // middle of the thumb arc feel like nothing was listening. The carve law
  // responds through the whole range, so the curve only needs enough bend
  // to keep straightlining calm.
  STEER_EXPO: 0.32,

  /** Downward travel that engages the check, and the travel that releases it. */
  BRAKE_ENGAGE: 46,
  BRAKE_RELEASE: 26,
  /**
   * How fast the vertical origin chases downward thumb drift, px/s. A thumb
   * arcing across the screen moves down at well under this and never brakes;
   * a deliberate push is several times it and always does.
   */
  BRAKE_ARC_FOLLOW: 70,

  /** Auto-tuck engages below this steer magnitude and drops above the second. */
  TUCK_ENTER: 0.30,
  TUCK_EXIT: 0.46,

  /** Upward flick speed that fires a pop, px/s. */
  POP_SPEED: 420,
  /** Minimum upward travel in the flick window. */
  POP_TRAVEL: 18,
  /** Flick window, seconds. */
  POP_WINDOW: 0.2,
}

const KEY_STEER_RAMP = 8.5 // units/s toward full lock

interface Pointer {
  id: number
  originX: number
  originY: number
  x: number
  y: number
  /** Seconds since this pointer went down. */
  age: number
  lastY: number
  /** Rolling upward travel inside the pop window. */
  flickTravel: number
  flickAge: number
  /** Latched check state, so it has hysteresis rather than chattering. */
  braking: boolean
}

export class Controls {
  private pointer: Pointer | null = null
  private keys = new Set<string>()
  private keySteer = 0
  private popLatched = false
  private tucking = true
  /** Scales touch distances so a phone and a desktop window feel the same. */
  private uiScale = 1

  /** True while any touch is down — used to hide the "tap to start" hint. */
  touching = false

  private readonly frame: InputFrame = { steer: 0, tuck: false, brake: false, pop: false }

  // The on-screen stick. Relative drag has no home position you can feel, so
  // without something to look at, finding "straight" is guesswork.
  private readonly ring: HTMLDivElement
  private readonly knob: HTMLDivElement

  constructor(private readonly el: HTMLElement) {
    el.addEventListener('pointerdown', this.onDown, { passive: false })
    el.addEventListener('pointermove', this.onMove, { passive: false })
    el.addEventListener('pointerup', this.onUp, { passive: false })
    el.addEventListener('pointercancel', this.onUp, { passive: false })
    el.addEventListener('contextmenu', (e) => e.preventDefault())
    window.addEventListener('keydown', this.onKeyDown)
    window.addEventListener('keyup', this.onKeyUp)

    this.ring = document.createElement('div')
    this.ring.className = 'stick-ring'
    this.knob = document.createElement('div')
    this.knob.className = 'stick-knob'
    document.body.append(this.ring, this.knob)

    this.resize()
    window.addEventListener('resize', this.resize)
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKeyDown)
    window.removeEventListener('keyup', this.onKeyUp)
    window.removeEventListener('resize', this.resize)
    this.ring.remove()
    this.knob.remove()
  }

  private resize = (): void => {
    // Reference is a 400px-wide phone; clamp so desktop isn't absurd.
    this.uiScale = clamp(Math.min(window.innerWidth, window.innerHeight) / 400, 0.75, 2.2)
    const d = TOUCH.STEER_RANGE * this.uiScale * 2
    this.ring.style.width = `${d}px`
    this.ring.style.height = `${d}px`
  }

  private onDown = (e: PointerEvent): void => {
    e.preventDefault()
    // A second finger anywhere is a pop. The steering thumb is already busy,
    // and a tap is far more reliable than a velocity-thresholded flick — which
    // had to be fast enough to register while not disturbing the line.
    if (this.pointer) {
      this.popLatched = true
      return
    }
    this.el.setPointerCapture?.(e.pointerId)
    this.pointer = {
      id: e.pointerId,
      originX: e.clientX,
      originY: e.clientY,
      x: e.clientX,
      y: e.clientY,
      age: 0,
      lastY: e.clientY,
      flickTravel: 0,
      flickAge: 0,
      braking: false,
    }
    this.touching = true
  }

  private onMove = (e: PointerEvent): void => {
    const p = this.pointer
    if (!p || p.id !== e.pointerId) return
    e.preventDefault()
    p.x = e.clientX
    p.y = e.clientY
  }

  private onUp = (e: PointerEvent): void => {
    if (!this.pointer || this.pointer.id !== e.pointerId) return
    this.el.releasePointerCapture?.(e.pointerId)
    this.pointer = null
    this.touching = false
  }

  private onKeyDown = (e: KeyboardEvent): void => {
    if (e.repeat) return
    this.keys.add(e.code)
    if (e.code === 'KeyW' || e.code === 'ArrowUp') this.popLatched = true
    // Don't let the page scroll out from under a game.
    if (
      e.code.startsWith('Arrow') ||
      e.code === 'Space' ||
      e.code === 'KeyA' ||
      e.code === 'KeyD' ||
      e.code === 'KeyS' ||
      e.code === 'KeyW'
    ) {
      e.preventDefault()
    }
  }

  private onKeyUp = (e: KeyboardEvent): void => {
    this.keys.delete(e.code)
  }

  isKeyDown(code: string): boolean {
    return this.keys.has(code)
  }

  /** Advance gesture recognition. Called once per rendered frame. */
  update(dt: number): void {
    const p = this.pointer
    if (p) {
      p.age += dt
      const dy = p.y - p.lastY

      // Pop: a fast upward flick. Tracked over a short window so a slow
      // upward drag doesn't accidentally launch you.
      p.flickAge += dt
      if (dy < 0) {
        p.flickTravel += -dy
      } else if (dy > 2) {
        p.flickTravel = 0
        p.flickAge = 0
      }
      if (p.flickAge > TOUCH.POP_WINDOW) {
        p.flickTravel = 0
        p.flickAge = 0
      }
      const flickSpeed = dt > 0 ? -dy / dt : 0
      if (
        flickSpeed > TOUCH.POP_SPEED * this.uiScale &&
        p.flickTravel > TOUCH.POP_TRAVEL * this.uiScale
      ) {
        this.popLatched = true
        p.flickTravel = 0
        p.flickAge = 0
        // Re-anchor vertically so the flick doesn't read as a sustained pull.
        p.originY = p.y
      }

      p.lastY = p.y

      // Re-anchor horizontally past full deflection.
      const range = TOUCH.STEER_RANGE * this.uiScale
      const dx = p.x - p.originX
      if (dx > range) p.originX = p.x - range
      else if (dx < -range) p.originX = p.x + range

      // --- Vertical: check, with the thumb's own arc filtered out ----------
      const down = p.y - p.originY
      if (down < 0) {
        // Never bank upward credit — the check is always a push from here.
        p.originY = p.y
      } else if (!p.braking) {
        // Let the origin chase slow downward drift, so an arcing thumb never
        // accumulates its way into a brake, but a deliberate push outruns it.
        //
        // dt is clamped: on one long frame — a GC pause, a chunk build, a
        // phone throttling — the follow would otherwise catch up tens of
        // pixels in a single step and swallow a push that had already
        // happened. Slow drift still gets followed over many frames; one
        // stalled frame can no longer eat the brake.
        const step = TOUCH.BRAKE_ARC_FOLLOW * this.uiScale * Math.min(dt, 0.05)
        p.originY += Math.min(down, step)
      }
      const held = p.y - p.originY
      if (p.braking) {
        if (held < TOUCH.BRAKE_RELEASE * this.uiScale) p.braking = false
      } else if (held > TOUCH.BRAKE_ENGAGE * this.uiScale) {
        p.braking = true
      }
    }

    // Keyboard steer ramps so it isn't a binary flick.
    const kLeft = this.keys.has('KeyA') || this.keys.has('ArrowLeft')
    const kRight = this.keys.has('KeyD') || this.keys.has('ArrowRight')
    const target = (kRight ? 1 : 0) - (kLeft ? 1 : 0)
    this.keySteer = moveTowards(this.keySteer, target, KEY_STEER_RAMP * dt)

    this.drawStick()
  }

  private drawStick(): void {
    const p = this.pointer
    if (!p) {
      this.ring.classList.remove('stick-on')
      this.knob.classList.remove('stick-on')
      return
    }
    this.ring.classList.add('stick-on')
    this.knob.classList.add('stick-on')
    this.ring.style.transform = `translate(${p.originX}px, ${p.originY}px) translate(-50%, -50%)`
    this.knob.style.transform = `translate(${p.x}px, ${p.y}px) translate(-50%, -50%)`
    this.knob.classList.toggle('stick-brake', p.braking)
  }

  /**
   * Latch the input for one physics tick. Called once per tick so the sim only
   * ever sees whole frames — which is what makes the Phase 4 input-stream
   * replay possible.
   */
  sample(): InputFrame {
    const f = this.frame
    f.steer = 0
    f.tuck = false
    f.brake = false
    f.pop = false

    const p = this.pointer
    if (p) {
      const range = TOUCH.STEER_RANGE * this.uiScale
      const dx = p.x - p.originX

      // Deadzone, rescaled so full travel still reaches full edge, then expo
      // so the centre is for trimming a line and the ends are for slarving.
      const raw = clamp(dx / range, -1, 1)
      const dz = TOUCH.STEER_DEADZONE
      const lin = Math.abs(raw) < dz ? 0 : (Math.abs(raw) - dz) / (1 - dz)
      const shaped = lin * (1 - TOUCH.STEER_EXPO) + lin * lin * lin * TOUCH.STEER_EXPO
      f.steer = shaped * Math.sign(raw)

      f.brake = p.braking
    }

    if (this.keySteer !== 0) f.steer = clamp(f.steer + this.keySteer, -1, 1)
    if (this.keys.has('KeyS') || this.keys.has('ArrowDown')) f.brake = true

    // Auto-tuck: you tuck unless you are braking or turning hard. Hysteresis,
    // because tuck is a real mechanical state (drag and plough) and flickering
    // it across a threshold would flicker your speed.
    const mag = Math.abs(f.steer)
    if (f.brake) this.tucking = false
    else if (this.tucking) this.tucking = mag < TOUCH.TUCK_EXIT
    else this.tucking = mag < TOUCH.TUCK_ENTER
    f.tuck = this.tucking || this.keys.has('Space')

    if (this.popLatched) {
      f.pop = true
      this.popLatched = false
    }

    // Quantise before the simulation ever sees it, so a recorded input stream
    // replays bit-identically. Quantising on the way into the recording instead
    // would mean the ghost rode slightly different inputs than the run that
    // produced it, and over ninety seconds that compounds into a different line.
    f.steer = quantiseSteer(f.steer)

    return f
  }
}
